import { PaginationButton, type PaginationButtonState } from '../PaginationButton/PaginationButton';
import styles from './Pagination.module.css';
import { pageWindow } from './pageWindow';

/**
 * Figma: `Pagination` (335:21297) — "Full pagination navigation bar for paged
 * content. Designed to sit at the bottom of a data table or card container (top
 * border removed, bottom corners rounded to match parent card)."
 *
 * The set has two variant properties, `Type` and `Shape`, each with a single
 * option (`Card button group right aligned`, `Square`). A one-option axis has
 * nothing to switch between, so neither becomes a prop — the same call
 * `Breadcrumbs` makes about its single-option `State`.
 *
 * The page buttons are data, not structure: Figma hard-codes five of them, which
 * an instance cannot vary, and the annotation says to "add/remove page number
 * buttons as needed". `pageCount` drives them here, five at a time — see
 * `pages`.
 */
export type PaginationProps = {
  /** Current page, 1-based. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  onPageChange?: (page: number) => void;
  /**
   * Which page numbers to render. Defaults to `pageWindow`: five around the
   * current page, the count the frame draws. Figma has no ellipsis or overflow
   * variant, and one button per page put 100 buttons in a horizontal scroll on a
   * 100-page list — which is what this used to default to.
   */
  pages?: number[];
  /**
   * Accessible name for the landmark. Not in Figma — a page can hold more than
   * one `<nav>`, so this one has to say which it is.
   */
  label?: string;
  /** Figma's `Previous` label, as a prop so it can be translated. */
  previousLabel?: string;
  /** Figma's `Next` label. */
  nextLabel?: string;
  /** Force every cell's state for documentation and visual regression only. */
  state?: PaginationButtonState;
  className?: string;
};

export function Pagination({
  page,
  pageCount,
  onPageChange,
  pages,
  label = 'Pagination',
  previousLabel = 'Previous',
  nextLabel = 'Next',
  state,
  className,
}: PaginationProps) {
  const numbers = pages ?? pageWindow(page, Math.max(0, pageCount));
  const atStart = page <= 1;
  const atEnd = page >= pageCount;

  return (
    <nav className={[styles.nav, className].filter(Boolean).join(' ')} aria-label={label}>
      {/*
       * The cells are a segmented control rather than a list of links, so they
       * sit directly in the group: it keeps them as real siblings, which is
       * what lets the shared-border rule in PaginationButton.module.css work.
       */}
      <div className={styles.group}>
        {/* Figma renders Previous as `State=Disabled` on page 1. */}
        <PaginationButton
          icon="leading"
          disabled={atStart}
          onClick={() => onPageChange?.(page - 1)}
          state={state}
        >
          {previousLabel}
        </PaginationButton>

        {numbers.map((n) => (
          <PaginationButton
            key={n}
            current={n === page}
            onClick={() => onPageChange?.(n)}
            /* Not in Figma — a bare digit is not a destination to a screen reader. */
            aria-label={`Go to page ${n}`}
            state={n === page ? undefined : state}
          >
            {n}
          </PaginationButton>
        ))}

        <PaginationButton
          icon="trailing"
          disabled={atEnd}
          onClick={() => onPageChange?.(page + 1)}
          state={state}
        >
          {nextLabel}
        </PaginationButton>
      </div>
    </nav>
  );
}
